import { useState, useEffect, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Lock, Loader2, CheckCircle, XCircle, Eye, EyeOff, ShieldCheck, KeyRound } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { validatePassword } from '@/lib/validators';
import { usePageTitle } from '../hooks/usePageTitle';
import { useForceLightMode } from '../hooks/useForceLightMode';

export default function ResetPasswordConfirm() {
  usePageTitle('Nova Senha');
  useForceLightMode();
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const token = searchParams.get('token');
  const email = searchParams.get('email');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Validadores de senha - mesmas regras do cadastro
  const passwordValidations = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasMatch: password === confirmPassword && confirmPassword.length > 0,
  };

  const isPasswordValid = passwordValidations.minLength && 
    passwordValidations.hasUppercase && 
    passwordValidations.hasLowercase && 
    passwordValidations.hasNumber;

  useEffect(() => {
    if (!token || !email) {
      setError(t('reset_password_confirm.invalid_link_error'));
    }
  }, [token, email, t]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    const validation = validatePassword(password);
    if (!validation.isValid) {
      setError(validation.error || t('reset_password_confirm.invalid_password'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('reset_password_confirm.passwords_not_match'));
      return;
    }

    setLoading(true);

    try {
      await api.post('/recuperar-senha/confirmar-reset', {
        email: email,
        token: token,
        novaSenha: password,
      });
      setSuccess(true);
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 3000);
    } catch (err: any) {
      const message = err.response?.data?.message || t('reset_password_confirm.error_generic');
      if (err.response?.status === 400 && message.includes('expirado')) {
        setError(t('reset_password_confirm.link_expired'));
      } else if (err.response?.status === 400) {
        setError(t('reset_password_confirm.link_invalid_or_used'));
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const isInvalidLink = !token || !email;

  return (
    <div className="relative flex min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="flex w-full flex-col lg:flex-row">
        {/* Lado esquerdo - Gradiente azul */}
        <div className="relative hidden flex-1 overflow-hidden lg:flex lg:items-center lg:justify-center">
          <div className="absolute inset-0 bg-gradient-to-br from-[#001A72] via-[#013EFB] to-[#0A7AFF]" />
          <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-10" />
          
          {/* Conteúdo decorativo */}
          <div className="relative z-10 p-12 text-center text-white">
            <div className="mx-auto mb-8 flex h-24 w-24 items-center justify-center rounded-full bg-white/10 backdrop-blur-sm">
              <KeyRound className="h-12 w-12 text-white" />
            </div>
            <h2 className="text-3xl font-bold mb-4">{t('reset_password_confirm.hero_title')}</h2>
            <p className="text-lg text-blue-100 max-w-md mx-auto">
              {t('reset_password_confirm.hero_description')}
            </p>
            
            {/* Indicadores de segurança */}
            <div className="mt-12 space-y-4 text-left max-w-sm mx-auto">
              <div className="flex items-center gap-3 text-blue-100">
                <ShieldCheck className="h-5 w-5 text-green-400" />
                <span className="text-sm">{t('reset_password_confirm.secure_connection')}</span>
              </div>
              <div className="flex items-center gap-3 text-blue-100">
                <Lock className="h-5 w-5 text-green-400" />
                <span className="text-sm">{t('reset_password_confirm.info_protected')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Lado direito - Formulário */}
        <div className="flex flex-1 items-center justify-center px-6 py-12 sm:px-12">
          <div className="w-full max-w-md">
            <img
              src="/logo-stant.svg"
              alt="Stant"
              className="mx-auto w-44 h-auto mb-8"
            />

            <Card className="bg-white shadow-2xl shadow-blue-500/10 border-0">
              {success ? (
                <>
                  <CardHeader className="text-center space-y-2 pb-2">
                    <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-green-400 to-emerald-500 shadow-lg shadow-green-500/30">
                      <CheckCircle className="h-10 w-10 text-white" />
                    </div>
                    <CardTitle className="text-2xl font-bold text-gray-900">{t('reset_password_confirm.success_title')}</CardTitle>
                    <CardDescription className="text-base">
                      {t('reset_password_confirm.success_description')}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="space-y-6 pt-4">
                    <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl">
                      <p className="text-sm text-green-800 text-center font-medium">
                        ✨ {t('reset_password_confirm.redirect_message')}
                      </p>
                    </div>

                    <div className="text-center">
                      <Link
                        to="/login"
                        className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
                      >
                        <ArrowLeft size={16} />
                        {t('reset_password_confirm.go_to_login')}
                      </Link>
                    </div>
                  </CardContent>
                </>
              ) : isInvalidLink ? (
                <>
                  <CardHeader className="text-center space-y-2 pb-2">
                    <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-red-400 to-rose-500 shadow-lg shadow-red-500/30">
                      <XCircle className="h-10 w-10 text-white" />
                    </div>
                    <CardTitle className="text-2xl font-bold text-gray-900">{t('reset_password_confirm.invalid_link_title')}</CardTitle>
                    <CardDescription className="text-base">
                      {t('reset_password_confirm.invalid_link_description')}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="space-y-6 pt-4">
                    <div className="p-4 bg-gradient-to-r from-red-50 to-rose-50 border border-red-200 rounded-xl">
                      <p className="text-sm text-red-800 text-center">
                        {t('reset_password_confirm.link_expiry_message')}
                      </p>
                    </div>

                    <div className="flex flex-col gap-3">
                      <Link to="/reset-password">
                        <Button className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg shadow-blue-500/25">
                          {t('reset_password_confirm.request_new_link')}
                        </Button>
                      </Link>
                      <div className="text-center">
                        <Link
                          to="/login"
                          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                        >
                          <ArrowLeft size={16} />
                          {t('reset_password.back_to_login')}
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </>
              ) : (
                <>
                  <CardHeader className="text-center space-y-2 pb-2">
                    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg shadow-blue-500/30">
                      <Lock className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-2xl font-bold text-gray-900">{t('reset_password_confirm.create_new_password')}</CardTitle>
                    <CardDescription className="text-base">
                      {t('reset_password_confirm.for_account')} <strong className="text-gray-700">{email}</strong>
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-4">
                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div className="space-y-2">
                        <Label htmlFor="password" className="text-gray-700 font-medium">{t('reset_password.new_password')}</Label>
                        <div className="relative">
                          <Input
                            id="password"
                            type={showPassword ? 'text' : 'password'}
                            placeholder={t('reset_password.new_password_placeholder')}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            disabled={loading}
                            autoComplete="new-password"
                            autoFocus
                            className="pr-10 h-12 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                          </button>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="confirmPassword" className="text-gray-700 font-medium">{t('reset_password_confirm.confirm_password')}</Label>
                        <div className="relative">
                          <Input
                            id="confirmPassword"
                            type={showConfirmPassword ? 'text' : 'password'}
                            placeholder={t('reset_password_confirm.confirm_password_placeholder')}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            disabled={loading}
                            autoComplete="new-password"
                            className="pr-10 h-12 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                          </button>
                        </div>
                      </div>

                      {/* Indicadores de validação - mesmas regras do cadastro */}
                      <div className="space-y-2 p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs font-medium text-gray-600 mb-2">{t('register.password_requirements')}</p>
                        <div className="flex items-center gap-2">
                          <div className={cn(
                            "w-4 h-4 rounded-full flex items-center justify-center text-xs",
                            passwordValidations.minLength 
                              ? "bg-green-500 text-white" 
                              : "bg-gray-200 text-gray-400"
                          )}>
                            {passwordValidations.minLength ? "✓" : ""}
                          </div>
                          <span className={cn(
                            "text-sm",
                            passwordValidations.minLength ? "text-green-700" : "text-gray-500"
                          )}>
                            {t('register.password_min_chars')}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className={cn(
                            "w-4 h-4 rounded-full flex items-center justify-center text-xs",
                            passwordValidations.hasUppercase 
                              ? "bg-green-500 text-white" 
                              : "bg-gray-200 text-gray-400"
                          )}>
                            {passwordValidations.hasUppercase ? "✓" : ""}
                          </div>
                          <span className={cn(
                            "text-sm",
                            passwordValidations.hasUppercase ? "text-green-700" : "text-gray-500"
                          )}>
                            {t('register.password_uppercase')}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className={cn(
                            "w-4 h-4 rounded-full flex items-center justify-center text-xs",
                            passwordValidations.hasLowercase 
                              ? "bg-green-500 text-white" 
                              : "bg-gray-200 text-gray-400"
                          )}>
                            {passwordValidations.hasLowercase ? "✓" : ""}
                          </div>
                          <span className={cn(
                            "text-sm",
                            passwordValidations.hasLowercase ? "text-green-700" : "text-gray-500"
                          )}>
                            {t('register.password_lowercase')}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className={cn(
                            "w-4 h-4 rounded-full flex items-center justify-center text-xs",
                            passwordValidations.hasNumber 
                              ? "bg-green-500 text-white" 
                              : "bg-gray-200 text-gray-400"
                          )}>
                            {passwordValidations.hasNumber ? "✓" : ""}
                          </div>
                          <span className={cn(
                            "text-sm",
                            passwordValidations.hasNumber ? "text-green-700" : "text-gray-500"
                          )}>
                            {t('register.password_number')}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 pt-1 border-t border-gray-200 mt-2">
                          <div className={cn(
                            "w-4 h-4 rounded-full flex items-center justify-center text-xs",
                            passwordValidations.hasMatch 
                              ? "bg-green-500 text-white" 
                              : "bg-gray-200 text-gray-400"
                          )}>
                            {passwordValidations.hasMatch ? "✓" : ""}
                          </div>
                          <span className={cn(
                            "text-sm",
                            passwordValidations.hasMatch ? "text-green-700" : "text-gray-500"
                          )}>
                            {t('reset_password_confirm.passwords_match')}
                          </span>
                        </div>
                      </div>

                      {error && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg animate-in fade-in slide-in-from-top-1">
                          <p className="text-sm text-red-600 text-center font-medium">{error}</p>
                        </div>
                      )}

                      <Button
                        type="submit"
                        className="w-full h-12 text-base font-semibold bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg shadow-blue-500/25 transition-all duration-200"
                        disabled={loading || !password || !confirmPassword || !isPasswordValid || !passwordValidations.hasMatch}
                      >
                        {loading ? (
                          <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                            {t('reset_password_confirm.resetting')}
                          </>
                        ) : (
                          <>
                            <ShieldCheck className="mr-2 h-5 w-5" />
                            {t('reset_password.reset_submit')}
                          </>
                        )}
                      </Button>

                      <div className="text-center pt-2">
                        <Link
                          to="/login"
                          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                        >
                          <ArrowLeft size={16} />
                          {t('reset_password.back_to_login')}
                        </Link>
                      </div>
                    </form>
                  </CardContent>
                </>
              )}
            </Card>

            {/* Footer */}
            <p className="text-center text-xs text-gray-400 mt-6">
              © {new Date().getFullYear()} {t('reset_password_confirm.footer')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
